import App from '@js/App';
import { PagePath } from '@routing/appUrls';
import { lazy, Suspense } from 'react';
import lazyRetry, { LazyRetryNames } from '@utils/lazyRetry';
import LoginPage from '@components/pages/loginPage/LoginPage';
import ProtectedRoute, { Roles } from '@routing/components/ProtectedRoute';
import RootErrorBoundary from '@components/features/errorBoundary/RootErrorBoundary';
import TrackPageVisits from '@components/features/tracking/components/TrackPageVisits';
import * as Sentry from '@sentry/react';
import { createBrowserRouter, Outlet } from 'react-router-dom';
import TraceShell from '@components/features/traceShell/TraceShell';

const DashboardPage = lazy(() => lazyRetry(() => import('@components/pages/dashboardPage/DashboardPage'), LazyRetryNames.Dashboard));
const ReleasesPage = lazy(() => lazyRetry(() => import('@components/pages/releasesPage/ReleasesPage'), LazyRetryNames.Releases));
const ListPage = lazy(() => lazyRetry(() => import('@components/pages/listPage/ListPage'), LazyRetryNames.List));
const DocumentPage = lazy(() => lazyRetry(() => import('@components/pages/documentPage/DocumentPage'), LazyRetryNames.Document));
const DocumentExternalViewPage = lazy(() => lazyRetry(() => import('@components/pages/documentExternalViewPage/DocumentExternalViewPage'), LazyRetryNames.DocumentExternalView));
const ModalExternalViewPage = lazy(() => lazyRetry(() => import('@components/pages/modalExternalViewPage/ModalExternalViewPage'), LazyRetryNames.ModalExternalView));
const FirstDocumentPage = lazy(() => lazyRetry(() => import('@components/pages/firstDocumentPage/FirstDocumentPage'), LazyRetryNames.FirstDocumentPage));
const AllDocumentProcessedPage = lazy(() => lazyRetry(() => import('@components/pages/allDocumentsProcessedPage/AllDocumentProcessedPage'), LazyRetryNames.AllDocumentProcessed));
const SettingsPage = lazy(() => lazyRetry(() => import('@components/pages/settingsPage/SettingsPage'), LazyRetryNames.Settings));
const SupportPage = lazy(() => lazyRetry(() => import('@components/pages/supportPage/SupportPage'), LazyRetryNames.Support));
const TechnicalInformation = lazy(() => lazyRetry(() => import('@components/pages/supportPage/components/TechnicalInformation'), LazyRetryNames.TechnicalInformation));
const Faq = lazy(() => lazyRetry(() => import('@components/pages/supportPage/components/Faq'), LazyRetryNames.Faq));
const Contact = lazy(() => lazyRetry(() => import('@components/pages/supportPage/components/Contact'), LazyRetryNames.Contact));
const Videos = lazy(() => lazyRetry(() => import('@components/pages/supportPage/components/Videos'), LazyRetryNames.Videos));
const Introduction = lazy(() => lazyRetry(() => import('@components/pages/supportPage/components/Introduction'), LazyRetryNames.Introduction));
const SearchPage = lazy(() => lazyRetry(() => import('@components/pages/searchPage/SearchPage'), LazyRetryNames.Search));
const FileHandlerPage = lazy(() => lazyRetry(() => import('@components/pages/fileHandlerPage/FileHandlerPage'), LazyRetryNames.FileHandler));
const Releases = lazy(() => lazyRetry(() => import('@components/pages/releasesPage/components/Releases'), LazyRetryNames.ReleasesHome));
const ReleasesSam = lazy(() => lazyRetry(() => import('@components/pages/releasesPage/components/ReleasesSam'), LazyRetryNames.ReleasesSam));
const GeneralSettings = lazy(() => lazyRetry(() => import('@components/pages/settingsPage/components/GeneralSettings'), LazyRetryNames.GeneralSettings));
const Configurations = lazy(() => lazyRetry(() => import('@components/pages/settingsPage/components/Configurations'), LazyRetryNames.Configurations));

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);
const router = sentryCreateBrowserRouter([
    {
        errorElement: <RootErrorBoundary />,
        element: (
            <TraceShell>
                <Outlet />
            </TraceShell>
        ),
        children: [
            {
                path: PagePath.login,
                element: (
                    <Suspense>
                        <TrackPageVisits>
                            <LoginPage />
                        </TrackPageVisits>
                    </Suspense>
                ),
            },
            {
                path: '/',
                element: (
                    <ProtectedRoute>
                        <TrackPageVisits>
                            <App />
                        </TrackPageVisits>
                    </ProtectedRoute>
                ),
                children: [
                    {
                        path: PagePath.search,
                        element: (
                            <Suspense>
                                <SearchPage />
                            </Suspense>
                        ),
                    },
                    {
                        path: PagePath.list,
                        element: (
                            <Suspense>
                                <ListPage />
                            </Suspense>
                        ),
                    },
                    {
                        path: PagePath.document,
                        element: (
                            <Suspense>
                                <DocumentPage />
                            </Suspense>
                        ),
                    },
                    {
                        path: PagePath.documentExternalView,
                        element: (
                            <Suspense>
                                <DocumentExternalViewPage />
                            </Suspense>
                        ),
                    },
                    {
                        path: PagePath.modalExternalView,
                        element: (
                            <Suspense>
                                <ModalExternalViewPage />
                            </Suspense>
                        ),
                    },
                    {
                        path: PagePath.firstDocument,
                        element: (
                            <Suspense>
                                <FirstDocumentPage />
                            </Suspense>
                        ),
                    },
                    {
                        path: PagePath.allDocumentsProcessed,
                        element: (
                            <Suspense>
                                <AllDocumentProcessedPage />
                            </Suspense>
                        ),
                    },
                    {
                        path: PagePath.fileHandler,
                        element: (
                            <Suspense>
                                <ProtectedRoute roles={[Roles.AllowFileHandler]}>
                                    <FileHandlerPage />
                                </ProtectedRoute>
                            </Suspense>
                        ),
                    },
                    {
                        path: PagePath.settings,
                        element: (
                            <Suspense>
                                <SettingsPage />
                            </Suspense>
                        ),
                        children: [
                            {
                                path: PagePath.configuration,
                                element: (
                                    <Suspense>
                                        <ProtectedRoute roles={[Roles.AllowRoutingTable]}>
                                            <Configurations />
                                        </ProtectedRoute>
                                    </Suspense>
                                ),
                            },
                            {
                                path: PagePath.settings,
                                element: (
                                    <Suspense>
                                        <GeneralSettings />
                                    </Suspense>
                                ),
                            },
                        ],
                    },
                    {
                        path: PagePath.support,
                        element: (
                            <Suspense>
                                <SupportPage />
                            </Suspense>
                        ),
                        children: [
                            {
                                path: PagePath.supportVideos,
                                element: (
                                    <Suspense>
                                        <Videos />
                                    </Suspense>
                                ),
                            },
                            {
                                path: PagePath.faq,
                                element: (
                                    <Suspense>
                                        <Faq />
                                    </Suspense>
                                ),
                            },
                            {
                                path: PagePath.contact,
                                element: (
                                    <Suspense>
                                        <Contact />
                                    </Suspense>
                                ),
                            },
                            {
                                path: PagePath.supportTechnicalInformation,
                                element: (
                                    <Suspense>
                                        <TechnicalInformation />
                                    </Suspense>
                                ),
                            },
                            {
                                path: PagePath.support,
                                element: (
                                    <Suspense>
                                        <Introduction />
                                    </Suspense>
                                ),
                            },
                        ],
                    },
                    {
                        path: PagePath.dashboard,
                        element: (
                            <Suspense>
                                <DashboardPage />
                            </Suspense>
                        ),
                    },
                    {
                        path: PagePath.releases,
                        element: (
                            <Suspense>
                                <ReleasesPage />
                            </Suspense>
                        ),
                        children: [
                            {
                                path: PagePath.releasesSam,
                                element: (
                                    <Suspense>
                                        <ReleasesSam />
                                    </Suspense>
                                ),
                            },
                            {
                                path: PagePath.releases,
                                element: (
                                    <Suspense>
                                        <Releases />
                                    </Suspense>
                                ),
                            },
                        ],
                    },
                ],
            },
        ],
    },
]);

export default router;
